/**
 * product.js
 *
 * Handles all client-side logic for the product page. Loaded only
 * on product pages via snippets/footer-scripts.liquid.
 *
 * Dependencies:
 *   - window.productVariants  — array of Shopify variant objects, serialised
 *                               inline by product.liquid before this script runs
 *   - window.sideCart         — public API exposed by side-cart.js:
 *                               { open(), fetchCart() }
 *
 * Sections in this file:
 *   1. Shared elements
 *   2. Variant / price switching
 *   3. Sticky nav visibility
 *   4. Quantity stepper
 *   5. Main ATC button feedback
 *   6. Sticky ATC modal (variant picker + add-to-cart)
 */
document.addEventListener('DOMContentLoaded', function () {

    // ── 1. Shared elements ───────────────────────────────────────────────────
    // These inputs are used by multiple sections below.
    var variantIdInput = document.getElementById('variant-id-input'); // hidden input that holds the active variant ID
    var qtyInput = document.getElementById('quantity');               // quantity number input


    // ── 2. Variant / price switching ─────────────────────────────────────────
    // When the customer selects a different variant option (size, color, etc.),
    // update the hidden variant ID input, sync the selected-value labels, and
    // re-render the price.
    var optionInputs = document.querySelectorAll('input[name^="option-"]');       // radio inputs for each option
    var selectedValueLabels = document.querySelectorAll('.product-option__selected-value'); // inline "selected: X" labels
    var priceContainer = document.getElementById('product-price-container');

    /**
     * Converts a price in cents to an HTML string with separate symbol and
     * cents spans. Omits the cents span when the value is a whole dollar.
     *
     * @param {number} cents
     * @returns {string} HTML string
     */
    function formatPrice(cents) {
        var dollars = (cents / 100).toFixed(2);
        var parts = dollars.split('.');
        var html = '<span class="product__price-symbol">$</span>' + parts[0];
        if (parts[1] && parts[1] !== '00') {
            html += '<span class="product__price-cents">' + parts[1] + '</span>';
        }
        return html;
    }

    /**
     * Renders the price for the given variant into priceContainer.
     * Shows a sale price + strikethrough compare-at price when applicable.
     *
     * @param {Object} variant — Shopify variant object
     */
    function updatePrice(variant) {
        if (!priceContainer || !variant) return;

        var html = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            // On sale: highlight current price and show compare-at as strikethrough
            html = '<ins class="product__price text--highlight">' + formatPrice(variant.price) + '</ins>';
            html += '<span class="product__price-compare">Reg. <del>' + formatPrice(variant.compare_at_price) + '</del></span>';
        } else {
            html = '<span class="product__price">' + formatPrice(variant.price) + '</span>';
        }
        priceContainer.innerHTML = html;
    }

    /**
     * Reads all checked option radio inputs, finds the matching variant in
     * window.productVariants, then updates the hidden variant ID input, the
     * selected-value labels, and the displayed price.
     */
    function updateVariant() {
        // Build a map of { optionIndex: selectedValue } from checked radios
        var selected = {};
        optionInputs.forEach(function (input) {
            if (input.checked) selected[input.dataset.optionIndex] = input.value;
        });

        // Find the variant whose options all match the current selection
        var variant = (window.productVariants || []).find(function (v) {
            return v.options.every(function (val, i) {
                return !selected.hasOwnProperty(String(i)) || selected[String(i)] === val;
            });
        });

        // Sync the "Selected: X" inline labels next to each option heading
        selectedValueLabels.forEach(function (label) {
            var idx = label.dataset.optionIndex;
            if (selected.hasOwnProperty(idx)) label.textContent = selected[idx];
        });

        if (variant && variantIdInput) {
            variantIdInput.value = variant.id;
            // Dispatch a change event so any other listeners (e.g. Shopify theme events) stay in sync
            variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
            updatePrice(variant);
        }
    }

    // Trigger updateVariant whenever any option radio changes
    optionInputs.forEach(function (input) {
        input.addEventListener('change', updateVariant);
    });


    // ── 3. Sticky nav visibility ─────────────────────────────────────────────
    // Show the sticky nav (which contains the sticky ATC button) only after the
    // main product section scrolls out of view. Uses IntersectionObserver where
    // available, with a scroll-event fallback for older browsers.
    var productMain = document.querySelector('.product__main');
    var stickyNav = document.querySelector('.sticky-nav');
    var toTopButton = document.getElementById('to-top');

    /**
     * Toggles the `is-visible` class on the sticky nav.
     * CSS handles the actual show/hide transition.
     *
     * @param {boolean} isVisible
     */
    function setStickyNavVisibility(isVisible) {
        if (!stickyNav) return;
        stickyNav.classList.toggle('is-visible', isVisible);
    }

    if (productMain && stickyNav) {
        if ('IntersectionObserver' in window) {
            // Show sticky nav when .product__main is no longer intersecting the viewport
            var observer = new IntersectionObserver(function (entries) {
                var entry = entries[0];
                setStickyNavVisibility(!entry.isIntersecting);
            }, { threshold: 0 });
            observer.observe(productMain);
        } else {
            // Fallback: recalculate on scroll and resize
            function updateStickyNavFromScroll() {
                var rect = productMain.getBoundingClientRect();
                var inView = rect.bottom > 0 && rect.top < window.innerHeight;
                setStickyNavVisibility(!inView);
            }
            window.addEventListener('scroll', updateStickyNavFromScroll, { passive: true });
            window.addEventListener('resize', updateStickyNavFromScroll);
            updateStickyNavFromScroll(); // run once on load
        }
    }

    // Smooth-scroll to the top of the page
    if (toTopButton) {
        toTopButton.addEventListener('click', function () {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }


    // ── 4. Quantity stepper ──────────────────────────────────────────────────
    // +/- buttons that increment or decrement the quantity input, clamped to
    // the input's min/max attributes.
    document.querySelectorAll('.product__qty-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            if (!qtyInput) return;
            var current = parseInt(qtyInput.value, 10) || 1;
            var min = parseInt(qtyInput.min, 10) || 1;
            var max = parseInt(qtyInput.max, 10) || Infinity;
            if (btn.dataset.action === 'plus') {
                qtyInput.value = Math.min(current + 1, max);
            } else {
                qtyInput.value = Math.max(current - 1, min);
            }
        });
    });


    // ── 5. Main ATC button feedback ──────────────────────────────────────────
    // Provides visual loading feedback on the main product form's submit button.
    //
    // How it works:
    //   - On form submit, add `is-adding` (shows spinner via CSS ::before).
    //   - side-cart.js intercepts the form submit, does the AJAX request, and
    //     calls button.disabled = true at the start and removes it in .finally().
    //   - A MutationObserver watches for that `disabled` attribute being removed,
    //     which signals the request completed. Then swap to `is-added` (shows a
    //     checkmark) and revert after 2 seconds.
    //   - CSS for `is-adding` and `is-added` lives in product.css.
    var productMain = document.querySelector('.product__main');
    var atcButton = productMain ? productMain.querySelector('.product__add-to-cart [type="submit"]') : null;
    var atcForm = productMain ? productMain.querySelector('form[action*="/cart/add"]') : null;

    if (atcForm && atcButton) {
        atcForm.addEventListener('submit', function () {
            atcButton.classList.add('is-adding');

            // Watch for side-cart.js to re-enable the button (disabled removed in .finally())
            var observer = new MutationObserver(function (mutations) {
                for (var i = 0; i < mutations.length; i++) {
                    if (mutations[i].attributeName === 'disabled' && !atcButton.disabled) {
                        observer.disconnect();
                        atcButton.classList.remove('is-adding');
                        atcButton.classList.add('is-added');
                        setTimeout(function () {
                            atcButton.classList.remove('is-added');
                        }, 2000);
                        return;
                    }
                }
            });
            observer.observe(atcButton, { attributes: true, attributeFilter: ['disabled'] });
        });
    }


    // ── 6. Sticky ATC modal ──────────────────────────────────────────────────
    // The sticky nav contains an "Add to Cart" button (#sticky-atc).
    //
    // - Single-variant products: clicking it calls the Shopify Cart API directly
    //   and opens the side cart on success.
    // - Multi-variant products: clicking it opens a bottom-sheet modal that lets
    //   the customer pick a variant before confirming.
    //
    // The modal is rendered conditionally in product.liquid (only when
    // product.variants.size > 1), so modal-related elements may be null here.
    var variants = window.productVariants || [];
    var hasVariants = variants.length > 1;
    var stickyAtcBtn = document.getElementById('sticky-atc');
    var modal = document.getElementById('sticky-atc-modal');
    var modalBackdrop = document.getElementById('sticky-atc-backdrop');
    var modalClose = document.getElementById('sticky-atc-modal-close');
    var modalConfirm = document.getElementById('sticky-atc-modal-confirm');
    var modalStatus = document.getElementById('sticky-atc-status'); // aria-live region for error/success messages

    // Labels restored after each add. Read from the markup rather than
    // hard-coded, so the buttons keep whatever product.liquid rendered
    // (e.g. "PRE ORDER" for a pre-order product).
    var stickyAtcLabel = stickyAtcBtn ? stickyAtcBtn.textContent.trim() : '';
    var modalConfirmLabel = modalConfirm ? modalConfirm.textContent.trim() : '';

    /**
     * Returns all currently focusable elements inside the modal, used to
     * constrain Tab/Shift+Tab focus when the modal is open.
     *
     * @returns {Element[]}
     */
    function getModalFocusables() {
        if (!modal) return [];
        return Array.from(modal.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
    }

    /**
     * Opens the variant picker modal and pre-selects whichever variant is
     * currently active in the main product form. Moves focus to the first
     * focusable element inside the modal.
     */
    function openModal() {
        if (!modal) return;

        // Mirror the currently selected variant into the modal's radio inputs
        var currentId = variantIdInput ? parseInt(variantIdInput.value, 10) : null;
        var current = currentId ? variants.find(function (v) { return v.id === currentId; }) : null;
        if (current) {
            current.options.forEach(function (val, i) {
                modal.querySelectorAll('input[name="sticky-option-' + (i + 1) + '"]').forEach(function (r) {
                    r.checked = r.value === val;
                    r.closest('.product-variants__swatch').classList.toggle('active', r.value === val);
                });
            });
        }

        modal.setAttribute('aria-hidden', 'false');
        modal.removeAttribute('inert');

        // Reset any previous status message and confirm button state
        if (modalStatus) modalStatus.hidden = true;
        if (modalConfirm) {
            modalConfirm.disabled = false;
            modalConfirm.textContent = modalConfirmLabel;
        }

        // Move focus into the modal for accessibility
        var firstFocusable = getModalFocusables()[0];
        if (firstFocusable) firstFocusable.focus();
    }

    /** Closes the modal. CSS transitions handle the slide-down animation. */
    function closeModal() {
        if (!modal) return;
        modal.setAttribute('aria-hidden', 'true');
        modal.setAttribute('inert', '');
    }

    /**
     * Reads the checked radio inputs inside the modal and returns the matching
     * variant object, or undefined if no match is found.
     *
     * @returns {Object|undefined}
     */
    function getModalVariant() {
        if (!modal) return null;
        var selected = {};
        modal.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
            selected[input.dataset.optionIndex] = input.value;
        });
        return variants.find(function (v) {
            return v.options.every(function (val, i) {
                return !selected.hasOwnProperty(String(i)) || selected[String(i)] === val;
            });
        });
    }

    /**
     * Reads the line item properties (`properties[…]` fields) from the main
     * product form, such as the pre-order ship date.
     *
     * Mirrors the property parsing in side-cart.js, which handles the form's own
     * submit. The properties are product-level, so they apply whichever variant
     * the sticky modal picks.
     *
     * @returns {Object} property name → value; empty when the form has none
     */
    function getFormProperties() {
        var properties = {};
        if (!atcForm) return properties;
        new FormData(atcForm).forEach(function (value, name) {
            var match = name.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
        });
        return properties;
    }

    /**
     * Builds the /cart/add.js `items` payload for a variant.
     *
     * The sticky bar and its variant modal bypass the product form entirely, so
     * they have to read the SafeOwl Care checkbox and the form's line item
     * properties themselves. Without this, the add-on would silently vanish for
     * anyone buying via the sticky bar, and a pre-order would arrive without its
     * ship date — on a separate cart line from the same variant added via the
     * form, since Shopify only merges lines whose properties match.
     *
     * @param {number} variantId
     * @param {number} quantity
     * @returns {Array<Object>}
     */
    function buildAddItems(variantId, quantity) {
        var care = window.SafeOwlCare;
        var toggle = document.querySelector('[data-care-addon]');
        var properties = getFormProperties();

        var device = { id: variantId, quantity: quantity };
        if (Object.keys(properties).length > 0) {
            device.properties = properties;
        }

        // Not requested — nothing to report.
        if (!toggle || !toggle.checked) {
            return [device];
        }

        // Requested but unfulfillable. Add the device alone rather than failing
        // the purchase, but never let that pass unreported.
        if (!care || !care.enabled) {
            console.error('[SafeOwl Care] Coverage was ticked but the add-on is inactive, so only the product was added. Run window.SafeOwlCare.diagnose() for details.');
            return [device];
        }

        // The label is resolved from the variant being added, not from the one
        // selected in the main form — the sticky modal has its own picker.
        var covers = care.deviceLabelForVariant(variantId, toggle.dataset.parentTitle || '');
        return care.buildItems(variantId, quantity, covers, properties);
    }

    /**
     * POSTs an item to the Shopify Cart API, together with SafeOwl Care coverage
     * when the add-on checkbox is ticked.
     * Returns a Promise that resolves with the parsed response JSON, or rejects
     * with an Error containing the Shopify error description.
     *
     * @param {number} variantId
     * @param {number} quantity
     * @returns {Promise<Object>}
     */
    function addToCart(variantId, quantity) {
        return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: buildAddItems(variantId, quantity) })
        }).then(function (res) {
            if (!res.ok) {
                return res.json().then(function (d) { throw new Error(d.description || 'Could not add to cart.'); });
            }
            return res.json();
        });
    }

    /**
     * Displays a status message in the modal's aria-live region.
     *
     * @param {string}  msg
     * @param {boolean} isError — true applies the error modifier class
     */
    function showStatus(msg, isError) {
        if (!modalStatus) return;
        modalStatus.textContent = msg;
        modalStatus.hidden = false;
        modalStatus.className = 'sticky-atc-modal__status' + (isError ? ' sticky-atc-modal__status--error' : ' sticky-atc-modal__status--success');
    }

    /**
     * Runs the main product form's own validation before a sticky add.
     *
     * The sticky bar and its modal bypass the form's submit, and with it the
     * browser's constraint validation. The Care Program's own page has required
     * fields (watch purchase date, where it was bought, and the terms checkbox —
     * see snippets/care-program-purchase-fields.liquid); without this check the
     * sticky bar would add the plan with them empty.
     *
     * reportValidity() scrolls to the first invalid field and shows its message.
     * The modal is closed first so it does not cover that field.
     *
     * @returns {boolean} true when the add may go ahead
     */
    function formIsValid() {
        if (!atcForm || atcForm.checkValidity()) return true;
        closeModal();
        atcForm.reportValidity();
        return false;
    }

    // Sticky ATC button click handler
    if (stickyAtcBtn) {
        stickyAtcBtn.addEventListener('click', function () {
            if (!formIsValid()) return;

            if (!hasVariants) {
                // Single-variant: add to cart immediately without opening the modal
                var variantId = variants.length ? variants[0].id : null;
                if (!variantId) return;
                var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
                stickyAtcBtn.disabled = true;
                stickyAtcBtn.textContent = 'Adding\u2026';
                addToCart(variantId, qty).then(function () {
                    stickyAtcBtn.textContent = 'Added!';
                    setTimeout(function () {
                        stickyAtcBtn.disabled = false;
                        stickyAtcBtn.textContent = stickyAtcLabel;
                    }, 2000);
                    // Refresh the side cart and slide it into view
                    if (window.sideCart) {
                        window.sideCart.fetchCart().then(function () { window.sideCart.open(); });
                    }
                }).catch(function (err) {
                    stickyAtcBtn.disabled = false;
                    stickyAtcBtn.textContent = stickyAtcLabel;
                    alert(err.message);
                });
            } else {
                // Multi-variant: open the picker modal so the customer can choose
                openModal();
            }
        });
    }

    // Sync the active-swatch highlight when a radio changes inside the modal
    if (modal) {
        modal.querySelectorAll('input[type="radio"]').forEach(function (input) {
            input.addEventListener('change', function () {
                modal.querySelectorAll('input[name="' + input.name + '"]').forEach(function (r) {
                    r.closest('.product-variants__swatch').classList.toggle('active', r.checked);
                });
            });
        });
    }

    // Close modal on backdrop click or close-button click
    if (modalBackdrop) modalBackdrop.addEventListener('click', closeModal);
    if (modalClose) modalClose.addEventListener('click', closeModal);

    // Keyboard handling for the modal:
    //   Escape — close the modal
    //   Tab / Shift+Tab — cycle focus within the modal (focus trap)
    document.addEventListener('keydown', function (e) {
        var isOpen = modal && modal.getAttribute('aria-hidden') === 'false';
        if (!isOpen) return;

        if (e.key === 'Escape') {
            closeModal();
            return;
        }

        if (e.key === 'Tab') {
            var focusables = getModalFocusables();
            if (focusables.length === 0) return;
            var first = focusables[0];
            var last = focusables[focusables.length - 1];
            if (e.shiftKey) {
                // Shift+Tab at the first element wraps to the last
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                // Tab at the last element wraps to the first
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    });

    // Confirm button inside the modal: validate selection, then add to cart
    if (modalConfirm) {
        modalConfirm.addEventListener('click', function () {
            if (!formIsValid()) return;

            var variant = getModalVariant();
            if (!variant) { showStatus('Please select a valid option combination.', true); return; }
            if (!variant.available) { showStatus('Sorry, this variant is sold out.', true); return; }

            var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
            modalConfirm.disabled = true;
            modalConfirm.textContent = 'Adding\u2026';
            if (modalStatus) modalStatus.hidden = true;

            addToCart(variant.id, qty).then(function () {
                closeModal();
                modalConfirm.disabled = false;
                modalConfirm.textContent = modalConfirmLabel;
                // Refresh the side cart and slide it into view
                if (window.sideCart) {
                    window.sideCart.fetchCart().then(function () { window.sideCart.open(); });
                }
            }).catch(function (err) {
                showStatus(err.message, true);
                modalConfirm.disabled = false;
                modalConfirm.textContent = modalConfirmLabel;
            });
        });
    }

});
